import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';

import type { ProjectionRow, SimulationType } from '@/services/inventorySimulationService';
import {
  buildPivotPeriods,
  formatPeriodAnchorDate,
  formatQty,
  type Granularity,
  type PivotPeriod,
  type ProtectionPeriodOptions,
} from '../utils/projection';

interface StockProjectionTableProps {
  projections: ProjectionRow[];
  simulationType: SimulationType;
  granularity: Granularity;
  protectionOptions?: ProtectionPeriodOptions;
  onSimulationTypeChange: (value: SimulationType) => void;
  onGranularityChange: (value: Granularity) => void;
  exportFilename?: string;
  loading?: boolean;
}

type MetricKey =
  | 'startingStock'
  | 'confirmedReceipts'
  | 'delayedReceipts'
  | 'suggestedReceipts'
  | 'projectedDemand'
  | 'endingStock'
  | 'safetyStock';

export function StockProjectionTable({
  projections,
  simulationType,
  granularity,
  protectionOptions,
  onSimulationTypeChange,
  onGranularityChange,
  exportFilename = 'simulacion-inventario',
  loading,
}: StockProjectionTableProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.toLowerCase().startsWith('it') ? 'it-IT' : 'es-ES';

  const periods = useMemo(
    () => buildPivotPeriods(projections, simulationType, granularity, protectionOptions),
    [projections, simulationType, granularity, protectionOptions],
  );

  const hasProtectionHighlight = periods.some((period) => period.inProtectionPeriod);

  const metrics: { key: MetricKey; label: string; highlight?: boolean }[] = [
    { key: 'startingStock', label: t('inventorySimulation.metrics.startingStock') },
    { key: 'confirmedReceipts', label: t('inventorySimulation.metrics.confirmedReceipts') },
    { key: 'delayedReceipts', label: t('inventorySimulation.metrics.delayedReceipts'), highlight: true },
    { key: 'suggestedReceipts', label: t('inventorySimulation.metrics.suggestedReceipts'), highlight: true },
    { key: 'projectedDemand', label: t('inventorySimulation.metrics.projectedDemand') },
    { key: 'endingStock', label: t('inventorySimulation.metrics.endingStock') },
    { key: 'safetyStock', label: t('inventorySimulation.metrics.safetyStock') },
  ];

  const cellValue = (period: PivotPeriod, key: MetricKey): number => period[key];

  const protectionColumnClass = (period: PivotPeriod): string =>
    period.inProtectionPeriod ? 'ring-1 ring-inset ring-emerald-200 bg-emerald-50/40' : '';

  const endingCellClass = (period: PivotPeriod): string => {
    if (period.stockout) return 'bg-red-100 text-red-800 font-semibold';
    if (period.belowSafety) return 'bg-amber-100 text-amber-800 font-medium';
    if (period.inProtectionPeriod) return 'bg-emerald-50/40 ring-1 ring-inset ring-emerald-200';
    return '';
  };

  const metricCellClass = (period: PivotPeriod, isEnding: boolean): string => {
    if (isEnding) return endingCellClass(period);
    return protectionColumnClass(period);
  };

  const handleExport = () => {
    const header = [t('inventorySimulation.pivot.metric'), ...periods.map((p) => p.label)];
    const rows = metrics.map((metric) => [
      metric.label,
      ...periods.map((p) => cellValue(p, metric.key)),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Simulacion');
    XLSX.writeFile(wb, `${exportFilename}.xlsx`);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">{t('inventorySimulation.pivot.title')}</h3>
          <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
            <button
              type="button"
              onClick={() => onGranularityChange('week')}
              className={`px-2.5 py-1 text-xs font-medium ${granularity === 'week' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {t('inventorySimulation.granularity.week')}
            </button>
            <button
              type="button"
              onClick={() => onGranularityChange('month')}
              className={`px-2.5 py-1 text-xs font-medium ${granularity === 'month' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {t('inventorySimulation.granularity.month')}
            </button>
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
            <button
              type="button"
              onClick={() => onSimulationTypeChange('WITH_SUGGESTED_ORDER')}
              className={`px-2.5 py-1 text-xs font-medium ${simulationType === 'WITH_SUGGESTED_ORDER' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {t('inventorySimulation.simulation.withOrder')}
            </button>
            <button
              type="button"
              onClick={() => onSimulationTypeChange('WITHOUT_SUGGESTED_ORDER')}
              className={`px-2.5 py-1 text-xs font-medium ${simulationType === 'WITHOUT_SUGGESTED_ORDER' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {t('inventorySimulation.simulation.withoutOrder')}
            </button>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={periods.length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-[#1681ba] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#1370a3] disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {t('common.export')}
          </button>
          </div>
        </div>
        {hasProtectionHighlight ? (
          <p className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
            <span className="inline-block h-3 w-3 rounded-sm border border-emerald-200 bg-emerald-50/80" aria-hidden />
            {t('inventorySimulation.pivot.protectionPeriodLegend')}
          </p>
        ) : null}
      </div>

      {loading ? (
        <div className="px-4 py-10 text-center text-sm text-gray-500">{t('common.loading')}</div>
      ) : periods.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-gray-500">
          {t('inventorySimulation.pivot.noProjection')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs tabular-nums">
            <thead>
              <tr className="bg-gray-50">
                <th className="sticky left-0 z-10 min-w-[180px] border-b border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600">
                  {t('inventorySimulation.pivot.metric')}
                </th>
                {periods.map((period) => (
                  <th
                    key={period.key}
                    title={period.inProtectionPeriod ? t('inventorySimulation.pivot.protectionPeriodColumn') : undefined}
                    className={`whitespace-nowrap border-b border-gray-200 px-3 py-2 text-right font-semibold text-gray-600 ${protectionColumnClass(period)}`}
                  >
                    <div className="flex flex-col items-end gap-0.5">
                      <span>{period.label}</span>
                      <span className="text-[10px] font-normal leading-none text-gray-400">
                        {formatPeriodAnchorDate(period.periodStart, dateLocale)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => (
                <tr
                  key={metric.key}
                  className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} ${metric.highlight ? 'bg-sky-50/60' : ''}`}
                >
                  <td
                    className={`sticky left-0 z-10 min-w-[180px] border-b border-gray-100 px-3 py-2 text-left font-medium text-gray-700 ${metric.highlight ? 'bg-sky-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                  >
                    {metric.label}
                  </td>
                  {periods.map((period) => {
                    const isEnding = metric.key === 'endingStock';
                    return (
                      <td
                        key={period.key}
                        className={`border-b border-gray-100 px-3 py-2 text-right text-gray-700 ${metricCellClass(period, isEnding)}`}
                      >
                        {formatQty(cellValue(period, metric.key))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
